'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Home, Zap, Boxes, MessageCircle } from 'lucide-react'

interface MobileTabBarProps {
  /** Company accounts have no Vault — hide that tab for them. */
  accountType?: string
}

/**
 * Persistent bottom navigation for mobile (<md).
 *
 * Added after the 2026-07-04 mobile audit: primary destinations were only
 * reachable through the hamburger dropdown (2 taps + 36px targets), and
 * messaging had no mobile entry point at all once the desktop dock was
 * hidden below md. Standard social-app pattern: 5 tabs, 44px+ targets,
 * safe-area padding for notched phones.
 */
export function MobileTabBar({ accountType }: MobileTabBarProps) {
  const pathname = usePathname()
  const isActive = (href: string) => pathname?.startsWith(href)

  const tabs = [
    { href: '/feed', label: 'Feed', Icon: Home },
    { href: '/signal', label: 'The Wire', Icon: Zap },
    // Jobs tab hidden from navbar per request.
    ...(accountType !== 'company'
      ? [{ href: '/vault', label: 'Vault', Icon: Boxes }]
      : []),
    { href: '/messages', label: 'Messages', Icon: MessageCircle },
  ]

  return (
    <nav
      aria-label="Primary"
      className="fixed inset-x-0 bottom-0 z-30 border-t border-[var(--color-border-default)] bg-[var(--color-bg-surface)]/95 backdrop-blur-md pb-[env(safe-area-inset-bottom)] md:hidden"
    >
      <div className="mx-auto flex max-w-md items-stretch justify-around">
        {tabs.map(({ href, label, Icon }) => {
          const active = isActive(href)
          return (
            <Link
              key={href}
              href={href}
              aria-label={label}
              aria-current={active ? 'page' : undefined}
              className={`flex min-h-12 flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-[10px] font-medium transition-colors ${
                active
                  ? 'text-[var(--color-brand-indigo)]'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
              }`}
            >
              <Icon className="h-5 w-5" aria-hidden="true" />
              <span>{label}</span>
            </Link>
          )
        })}
      </div>
    </nav>
  )
}
